import { Badge, EmptyState, Panel, cn, type Tone } from "@/components/ui";
import { formatDayHeading, formatTime } from "@/lib/format";
import type { ActivityEvent, ActivityKind } from "@/lib/types";
import { useCase } from "@/components/case/case-context";

const KIND_TONE: Record<ActivityKind, Tone> = {
  decision: "accent",
  model: "neutral",
  evidence: "neutral",
  adjustment: "caution",
  note: "neutral",
  stage: "positive",
};

/**
 * The audit trail. Written for someone reconstructing a decision months later,
 * so every entry that changed a value shows the old value, the new value and
 * the stated reason — the three things a reviewer asks for.
 */
export function ActivityTimeline() {
  const { activity } = useCase();

  if (activity.length === 0) {
    return (
      <Panel>
        <EmptyState title="No activity yet" />
      </Panel>
    );
  }

  const days = groupByDay(activity);

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {days.map(([day, events]) => (
        <section key={day}>
          <h2 className="eyebrow mb-2">{day}</h2>
          <Panel>
            <ol>
              {events.map((event) => (
                <li key={event.id} className="flex gap-4 border-b border-line px-4 py-3 last:border-0">
                  <time
                    dateTime={event.at}
                    className="numeric w-11 shrink-0 pt-0.5 text-xs text-ink-faint"
                  >
                    {formatTime(event.at)}
                  </time>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-ink">{event.title}</span>
                      <Badge tone={KIND_TONE[event.kind]}>{event.kind}</Badge>
                    </div>

                    {event.from !== undefined && event.to !== undefined ? (
                      <p className="numeric mt-1 flex items-center gap-1.5 text-sm">
                        <span className="text-ink-muted line-through decoration-line-strong">
                          {event.from}
                        </span>
                        <span aria-hidden className="text-ink-faint">
                          →
                        </span>
                        <span className="font-medium text-ink">{event.to}</span>
                      </p>
                    ) : null}

                    {event.detail ? (
                      <p className="mt-1 text-xs leading-relaxed text-ink-muted">{event.detail}</p>
                    ) : null}

                    {event.reason ? (
                      <p className={cn("mt-1 text-xs leading-relaxed text-ink-muted")}>
                        <span className="text-ink-faint">Reason: </span>
                        {event.reason}
                      </p>
                    ) : null}

                    <p className="mt-1 text-[0.6875rem] text-ink-faint">{event.actor}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Panel>
        </section>
      ))}
    </div>
  );
}

/** Events arrive newest first; day order and within-day order both follow. */
function groupByDay(events: ActivityEvent[]): [string, ActivityEvent[]][] {
  const days = new Map<string, ActivityEvent[]>();
  for (const event of events) {
    const key = formatDayHeading(event.at);
    const bucket = days.get(key);
    if (bucket) bucket.push(event);
    else days.set(key, [event]);
  }
  return [...days.entries()];
}
